import {
  convertEventSelectToTarget,
  FormFeedBack,
  IOptions,
  ModalBase,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllGroupsNotPaginatedUseCaseInterface,
  ImportPersonsFromAnotherGroupUseCaseInterface,
  ImportPlacesFromAnotherGroupUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useMemo, useState } from 'react';
import { Button, Col, Spinner, Form, Row } from 'reactstrap';
import * as Yup from 'yup';

interface IProps {
  isOpen: boolean;
  toggle: () => void;
  importedEntitiesTitle: string;
  buttonsLoading: boolean;
  requestImportEntitiesFromAnotherGroup: (
    params:
      | ImportPersonsFromAnotherGroupUseCaseInterface.Params
      | ImportPlacesFromAnotherGroupUseCaseInterface.Params,
  ) => void;
  targetGroupId: string;
  importedPersonType?: PROFILE_TYPE;
}

export const ImportEntitiesFromAnotherGroupModal = ({
  isOpen,
  toggle,
  importedEntitiesTitle,
  buttonsLoading,
  requestImportEntitiesFromAnotherGroup,
  targetGroupId,
  importedPersonType,
}: IProps) => {
  // Hooks
  const { groupService } = useService();
  const { getLegends } = useLegends();

  // States
  const [groupSelectStatus, setGroupSelectStatus] = useState(STATUSPAGE.DONE);
  const [groupSelectOptions, setGroupSelectOptions] =
    useState<GetAllGroupsNotPaginatedUseCaseInterface.Result>(null);
  const groupOptions: IOptions[] = useMemo(() => {
    return (
      groupSelectOptions?.data?.map((group) => {
        const { id, name } = group;
        if (id === targetGroupId) return;
        return {
          label: name,
          value: id,
        };
      }) || []
    );
  }, [groupSelectOptions, targetGroupId]);

  // Methods
  const requestGetAllGroupsForSelectOptions = useCallback(
    async (params: GetAllGroupsNotPaginatedUseCaseInterface.Params) => {
      setGroupSelectStatus(STATUSPAGE.LOADING);
      await groupService
        .getAllGroupsNotPaginated(params)
        .then((res) => setGroupSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setGroupSelectStatus(null);
        })
        .finally(() => setGroupSelectStatus(STATUSPAGE.DONE));
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      group: null,
    },

    validationSchema: Yup.object({
      group: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const groupsValues = {
        sourceGroup: values.group?.value,
        targetGroup: targetGroupId,
      };

      const valuesToSubmit = importedPersonType
        ? {
            personType: importedPersonType,
            ...groupsValues,
          }
        : {
            ...groupsValues,
          };

      requestImportEntitiesFromAnotherGroup(valuesToSubmit);
    },
  });

  return (
    <ModalBase.Wrapper
      loading={false}
      isOpen={isOpen}
      toggle={toggle}
      zIndex={10000}
      size="lg"
    >
      <ModalBase.Header toggle={toggle} withBorderBottom>
        Importar {importedEntitiesTitle} de outro {getLegends('GRUPO')}
      </ModalBase.Header>
      <ModalBase.Body>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col md={9} className="mb-3">
              <SelectRemote
                isClearable
                name="group"
                clearOptions={() => setGroupSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setGroupSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllGroupsForSelectOptions({
                    query: searchString,
                  });
                }}
                inputId={'group'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('group', e),
                  );
                }}
                onBlur={(e) => {
                  setGroupSelectOptions(null);
                }}
                options={groupOptions}
                placeholder={`Selecione um ${getLegends('GRUPO')}`}
                value={validation.values.group}
                loading={groupSelectStatus === STATUSPAGE.LOADING}
                isInvalid={
                  !!(validation.touched?.group && validation.errors?.group)
                }
              />
              <FormFeedBack
                error={String(validation.errors?.group)}
                isInvalid={
                  !!(validation.touched.group && validation.errors?.group)
                }
              />
            </Col>
            <Col
              md={3}
              className="d-flex justify-content-center align-items-start"
            >
              <Button type="submit" disabled={buttonsLoading}>
                {buttonsLoading && (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                )}
                Iniciar Importação
              </Button>
            </Col>
          </Row>
        </Form>
      </ModalBase.Body>
    </ModalBase.Wrapper>
  );
};
